import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppState";
import { POPUP_SECTIONS, settingIndex } from "../../constant/constant";
import {
  getDraggablePopupBackgroundStyle,
} from "../../helper";
import {
  getStepSectionsForPopup,
  stripShellDimensionsFromCustomCSS,
} from "../../utils/popup-step-sections";
import {
  ackDeselectToParent,
  ackSelectBlockToParent,
  isDeselectBlockMessage,
  parseSelectBlockMessage,
} from "../../utils/preview-selection-messages";
import { shouldShowPopupWatermark } from "../../utils/popup-watermark";
import { COUNTDOWN_DISPLAY_LAYOUT, resolveCountdownDisplay } from "../../utils/countdownDisplay";
import TextBlock from "../../components/Preview/Draggable/Block/TextBlock";
import CountdownBlock from "../../components/Preview/Draggable/Block/CountdownBlock";
import PopupWatermark from "../../components/Preview/PopupWatermark";
import AiTrustSignalsRow from "../ai-recommendation-upsell/components/AiTrustSignalsRow";
import AiBrandAttribution from "../ai-recommendation-upsell/components/AiBrandAttribution";
import ProductRecommendationSlider from "./components/ProductRecommendationSlider";
import ProductRecommendationPitchLine from "./components/ProductRecommendationPitchLine";
import useAiRecommendationCopy from "../ai-recommendation-upsell/hooks/useAiRecommendationCopy";
import { isPopupRuntimeContentLoading } from "../../utils/popup-runtime-content-loading";
import {
  getProductRecommendationLayoutOptions,
  indexProductRecommendationSections,
} from "./mapProductRecommendationSchema";
import {
  resolvePopupThemeBorderRadius,
} from "../../utils/resolve-popup-theme-border-radius.js";
import {
  buildPopupThemeCssVariables,
  resolvePopupThemeTokens,
} from "../../utils/resolve-popup-theme-tokens.js";
import { isSectionVisible, isSectionVisibleForViewport } from "../../../shared/section-visibility.js";
import styles from "./ProductRecommendationPopup.module.css";

const DEFAULT_SHELL_DESKTOP_WIDTH_PX = 840;

function PreviewSlot({ children, isSelected, onClick, className = "" }) {
  return (
    <div
      className={`${styles.slot} ${isSelected ? styles.slotSelected : ""} ${className}`.trim()}
      onClick={(e) => {
        const target = e.target;
        const isInputField =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.contentEditable === "true" ||
          target.closest("input, textarea, select, [contenteditable]");
        const isInteractive =
          target.tagName === "BUTTON" ||
          target.closest("button, a, [role='button']");
        if (!isInputField && !isInteractive) onClick?.(e);
      }}
    >
      {children}
    </div>
  );
}

const ProductRecommendationPopup = ({ popupData, popupState, setPopupState }) => {
  const {
    appState,
    isMobile: viewportIsMobile,
    previewDeviceIsMobile,
    clickTriggerEvents,
    setClickTriggerEvents,
    popupGlobalData,
  } = useApp();
  const isMobile =
    appState?.is_preview_mode && typeof previewDeviceIsMobile === "boolean"
      ? previewDeviceIsMobile
      : viewportIsMobile;

  const location = useLocation();
  const navigate = useNavigate();
  const popupRef = useRef(null);
  const finishCloseRef = useRef(null);
  const closeAnimationDoneRef = useRef(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);

  const popupType = popupData?.popUpType;
  const popupId = popupData?._id;

  const activeStep = useMemo(() => {
    const fromPopupState = popupState?.activeStep;
    if (typeof fromPopupState === "number" && !Number.isNaN(fromPopupState)) {
      return fromPopupState;
    }
    if (
      appState?.is_preview_mode &&
      typeof appState?.activeStep === "number" &&
      !Number.isNaN(appState.activeStep)
    ) {
      return appState.activeStep;
    }
    return 0;
  }, [popupState?.activeStep, appState?.is_preview_mode, appState?.activeStep]);

  const stepSections = useMemo(
    () => getStepSectionsForPopup(popupData, activeStep),
    [popupData, activeStep]
  );

  const mapped = useMemo(
    () => indexProductRecommendationSections(stepSections),
    [stepSections]
  );

  const pitchCopy = useAiRecommendationCopy(mapped.pitchLine, popupData);
  const runtimeContentLoading =
    !appState?.is_preview_mode && isPopupRuntimeContentLoading(popupData);
  const pitchLoading = pitchCopy.loading;

  const layoutOptions = useMemo(
    () => getProductRecommendationLayoutOptions(popupData),
    [popupData]
  );

  const [selectedBlock, setSelectedBlock] = useState({});
  const [selectedSub, setSelectedSub] = useState({ type: null, index: -1 });
  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const popupSettings = popupData?.schema?.[activeStep]?.popupSettings || {};
  const shellCss =
    popupSettings?.customCSS?.[isMobile ? "mobile" : "desktop"] || {};

  const shellBorderRadiusPx = useMemo(
    () =>
      resolvePopupThemeBorderRadius(popupData, {
        activeStep,
        isMobile,
      }),
    [popupData, activeStep, isMobile]
  );

  const themeTokens = useMemo(
    () =>
      resolvePopupThemeTokens(popupData, {
        activeStep,
        isMobile,
      }),
    [popupData, activeStep, isMobile]
  );

  const themeCssVariables = useMemo(
    () => buildPopupThemeCssVariables(themeTokens),
    [themeTokens]
  );

  const shellStyle = useMemo(() => {
    const stripped = stripShellDimensionsFromCustomCSS(shellCss);
    const resolvedWidth =
      isMobile && (shellCss.widthUnit === "%" || shellCss.width === "100%")
        ? "100%"
        : shellCss.widthUnit === "%"
          ? shellCss.width
          : `${shellCss.width || DEFAULT_SHELL_DESKTOP_WIDTH_PX}px`;
    const mobileDrawerShell = isMobile;
    return {
      ...getDraggablePopupBackgroundStyle(popupSettings, isMobile),
      ...stripped,
      ...themeCssVariables,
      borderRadius: mobileDrawerShell ? "20px 20px 0 0" : shellBorderRadiusPx,
      width: mobileDrawerShell ? "100%" : resolvedWidth,
      maxWidth: "100%",
      ...(themeTokens.fontFamily ? { fontFamily: themeTokens.fontFamily } : {}),
      color: themeTokens.text,
      boxShadow: mobileDrawerShell
        ? "0 -8px 32px rgba(15, 23, 42, 0.14)"
        : stripped.boxShadow || "0 28px 56px rgba(15, 23, 42, 0.14)",
      animation: mobileDrawerShell ? "none" : stripped.animation,
    };
  }, [
    popupSettings,
    isMobile,
    shellCss,
    shellBorderRadiusPx,
    themeCssVariables,
    themeTokens.fontFamily,
    themeTokens.text,
  ]);

  const handleBlockClick = useCallback(
    (type, index) => {
      if (!appState?.is_preview_mode) return;
      const prev = selectedBlockRef.current;
      if (prev.type === type && prev.index === index) return;
      ackSelectBlockToParent({ type, index });
      setSelectedBlock({ type, index });
      setSelectedSub({ type: null, index: -1 });
    },
    [appState?.is_preview_mode]
  );

  const handleSubBlockClick = useCallback(
    (type, subIndex, productIndex) => {
      if (!appState?.is_preview_mode) return;
      const productIndexInSchema = mapped.product?.index ?? -1;
      if (productIndexInSchema < 0) return;
      ackSelectBlockToParent({
        type,
        index: productIndexInSchema,
        subIndex,
      });
      setSelectedBlock({ type: POPUP_SECTIONS.PRODUCT, index: productIndexInSchema });
      setSelectedSub({ type, index: subIndex, productIndex });
    },
    [appState?.is_preview_mode, mapped.product?.index]
  );

  const handleOutSideBlockClick = useCallback(() => {
    setSelectedBlock({});
    setSelectedSub({ type: null, index: -1 });
    if (appState?.is_preview_mode) {
      ackDeselectToParent();
    }
  }, [appState?.is_preview_mode]);

  useEffect(() => {
    if (!appState?.is_preview_mode) return undefined;

    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleOutSideBlockClick();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [appState?.is_preview_mode, handleOutSideBlockClick]);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (isDeselectBlockMessage(data)) {
        setSelectedBlock({});
        setSelectedSub({ type: null, index: -1 });
        return;
      }

      if (data.event === "sectionCreated") {
        const { type, index } = data;
        if (index === settingIndex) {
          handleOutSideBlockClick();
        } else if (typeof index === "number" && index !== settingIndex) {
          setSelectedBlock({ type, index });
          handleBlockClick(type, index);
        }
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection) return;

      const { type, index, subIndex } = selection;
      if (index === settingIndex || index === -123) {
        handleOutSideBlockClick();
        return;
      }
      if (typeof subIndex === "number" && subIndex >= 0) {
        setSelectedBlock({ type: POPUP_SECTIONS.PRODUCT, index });
        setSelectedSub({ type, index: subIndex });
        return;
      }
      setSelectedBlock({ type, index });
      setSelectedSub({ type: null, index: -1 });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleBlockClick, handleOutSideBlockClick]);

  const finishClose = useCallback(
    async (callAddToCartEvents = true) => {
      setPopupState((prev) => ({
        ...prev,
        [`${popupType}_${popupId}`]: {
          ...prev?.[`${popupType}_${popupId}`],
          openPopup: false,
          drawerClosing: false,
        },
      }));

      if (location?.hash !== "") {
        navigate(`${location?.pathname}`);
      }

      if (
        clickTriggerEvents?.[`${popupType}-${popupId}`] &&
        callAddToCartEvents &&
        typeof clickTriggerEvents?.[`${popupType}-${popupId}`] === "function"
      ) {
        clickTriggerEvents?.[`${popupType}-${popupId}`]();
        setTimeout(() => {
          setClickTriggerEvents((prev) => {
            const temp = { ...prev };
            delete temp[`${popupType}-${popupId}`];
            return temp;
          });
        }, 100);
      }
    },
    [
      setPopupState,
      location,
      navigate,
      popupType,
      popupId,
      popupData,
      clickTriggerEvents,
      setClickTriggerEvents,
    ]
  );
  finishCloseRef.current = finishClose;

  const handleClose = useCallback(
    async (callAddToCartEvents = true) => {
      if (isDrawerClosing) return;

      if (isMobile) {
        setIsDrawerClosing(true);
        setPopupState((prev) => ({
          ...prev,
          [`${popupType}_${popupId}`]: {
            ...prev?.[`${popupType}_${popupId}`],
            drawerClosing: true,
          },
        }));
        return;
      }

      await finishClose(callAddToCartEvents);
    },
    [
      isMobile,
      isDrawerClosing,
      finishClose,
      setPopupState,
      popupType,
      popupId,
    ]
  );

  const handleDrawerAnimationEnd = useCallback((event) => {
    if (event.target !== event.currentTarget) return;
    const animationName = String(event.animationName || "");
    if (!animationName.includes("SlideDown")) return;
    if (closeAnimationDoneRef.current) return;
    closeAnimationDoneRef.current = true;
    setIsDrawerClosing(false);
    finishCloseRef.current?.();
  }, []);

  useEffect(() => {
    if (!isDrawerClosing) {
      closeAnimationDoneRef.current = false;
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (closeAnimationDoneRef.current) return;
      closeAnimationDoneRef.current = true;
      setIsDrawerClosing(false);
      finishCloseRef.current?.();
    }, 380);

    return () => window.clearTimeout(timer);
  }, [isDrawerClosing]);

  useEffect(() => {
    const root = document.querySelector("#popup-preview-shadow-root");
    if (root) {
      root.style.zIndex = `${popupSettings?.zIndex ?? 9999999999999}`;
    }
  }, [activeStep, popupSettings?.zIndex]);

  const hasInlineBrandAttribution = Boolean(mapped.brandAttribution);

  const showFloatingWatermark = useMemo(
    () =>
      !hasInlineBrandAttribution &&
      shouldShowPopupWatermark(
        popupData,
        activeStep,
        appState?.watermark,
        appState?.is_preview_mode,
        appState?.shop
      ),
    [
      hasInlineBrandAttribution,
      popupData,
      activeStep,
      appState?.watermark,
      appState?.is_preview_mode,
      appState?.shop,
    ]
  );

  const countdownDisplay = mapped.countdown?.section
    ? resolveCountdownDisplay(mapped.countdown.section, popupData)
    : null;
  const showCountdownBar =
    mapped.countdown &&
    isSectionVisible(mapped.countdown.section) &&
    countdownDisplay?.layout === COUNTDOWN_DISPLAY_LAYOUT.BAR;

  const isSelected = (entry) =>
    entry &&
    selectedBlock.type === entry.section?.type &&
    selectedBlock.index === entry.index;

  const isTrustVisible =
    mapped.trust &&
    layoutOptions.showTrustBadges !== false &&
    (isSectionVisibleForViewport(mapped.trust.section, isMobile) ||
      isSectionVisible(mapped.trust.section));

  const isBrandVisible =
    mapped.brandAttribution &&
    (isSectionVisibleForViewport(mapped.brandAttribution.section, isMobile) ||
      isSectionVisible(mapped.brandAttribution.section));

  return (
    <div
      className={`${styles.shellWrap} ${isMobile ? styles.shellWrapDrawer : ""} ${isDrawerClosing ? styles.shellWrapDrawerClosing : ""}`.trim()}
      ref={popupRef}
      onAnimationEnd={isMobile ? handleDrawerAnimationEnd : undefined}
    >
      <button
        type="button"
        className={`${styles.shellClose} ${isMobile ? styles.shellCloseDrawer : ""}`.trim()}
        aria-label="Close popup"
        onClick={(e) => {
          e.stopPropagation();
          if (appState?.is_preview_mode) return;
          handleClose();
        }}
        style={
          appState?.is_preview_mode
            ? { pointerEvents: "none", cursor: "default" }
            : undefined
        }
      >
        ×
      </button>
      <div
        className={`${styles.shell} ${isMobile ? styles.shellDrawer : ""}`.trim()}
        style={shellStyle}
        data-testid="product-recommendation-popup"
      >
        {showCountdownBar ? (
          <PreviewSlot
            className={styles.countdownSlot}
            isSelected={isSelected(mapped.countdown)}
            onClick={() =>
              handleBlockClick(
                mapped.countdown.section.type,
                mapped.countdown.index
              )
            }
          >
            <CountdownBlock
              popupData={popupData}
              section={mapped.countdown.section}
            />
          </PreviewSlot>
        ) : null}

        {mapped.pitchLine && isSectionVisible(mapped.pitchLine.section) ? (
          <PreviewSlot
            className={styles.pitchSlot}
            isSelected={isSelected(mapped.pitchLine)}
            onClick={() =>
              handleBlockClick(
                mapped.pitchLine.section.type,
                mapped.pitchLine.index
              )
            }
          >
            <ProductRecommendationPitchLine
              section={mapped.pitchLine.section}
              popupData={popupData}
              activeStep={activeStep}
              index={mapped.pitchLine.index}
              displayText={
                mapped.pitchLine.section?.setting?.role === "aiBanner"
                  ? pitchCopy.displayText
                  : mapped.pitchLine.section?.text
              }
              loading={
                mapped.pitchLine.section?.setting?.role === "aiBanner"
                  ? pitchLoading
                  : false
              }
              isMobile={isMobile}
            />
          </PreviewSlot>
        ) : null}

        {mapped.headline && isSectionVisible(mapped.headline.section) ? (
          <PreviewSlot
            className={styles.headlineSlot}
            isSelected={isSelected(mapped.headline)}
            onClick={() =>
              handleBlockClick(
                mapped.headline.section.type,
                mapped.headline.index
              )
            }
          >
            <TextBlock
              popupData={popupData}
              activeStep={activeStep}
              section={mapped.headline.section}
              index={mapped.headline.index}
            />
          </PreviewSlot>
        ) : null}

        <div
          className={styles.body}
          data-pr-drawer-body
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleOutSideBlockClick();
            }
          }}
        >
          {mapped.product && isSectionVisible(mapped.product.section) ? (
            <PreviewSlot
              className={styles.productGridSlot}
              isSelected={isSelected(mapped.product)}
              onClick={() =>
                handleBlockClick(
                  mapped.product.section.type,
                  mapped.product.index
                )
              }
            >
              <ProductRecommendationSlider
                section={mapped.product.section}
                popupData={popupData}
                popupState={popupState}
                setPopupState={setPopupState}
                activeStep={activeStep}
                handleClose={handleClose}
                isPreviewMode={appState?.is_preview_mode}
                isMobile={isMobile}
                contentLoading={runtimeContentLoading}
                selectedSubType={selectedSub.type}
                selectedSubIndex={selectedSub.index}
                onSubBlockClick={handleSubBlockClick}
              />
            </PreviewSlot>
          ) : null}
        </div>

        {(isTrustVisible || isBrandVisible) && isMobile ? (
          <div className={styles.drawerFooter}>
            {isTrustVisible ? (
              <PreviewSlot
                className={styles.trustRow}
                isSelected={isSelected(mapped.trust)}
                onClick={() =>
                  handleBlockClick(
                    mapped.trust.section.type,
                    mapped.trust.index
                  )
                }
              >
                <AiTrustSignalsRow
                  section={mapped.trust.section}
                  text={mapped.trust.section?.text}
                  isMobile={isMobile}
                />
              </PreviewSlot>
            ) : null}

            {isBrandVisible ? (
              <PreviewSlot
                className={styles.footerRow}
                isSelected={isSelected(mapped.brandAttribution)}
                onClick={() =>
                  handleBlockClick(
                    mapped.brandAttribution.section.type,
                    mapped.brandAttribution.index
                  )
                }
              >
                <AiBrandAttribution
                  section={mapped.brandAttribution.section}
                  forceShowAttribution={appState?.is_preview_mode}
                  forceShowBadge={appState?.is_preview_mode}
                />
              </PreviewSlot>
            ) : null}
          </div>
        ) : (
          <>
            {isTrustVisible ? (
              <PreviewSlot
                className={styles.trustRow}
                isSelected={isSelected(mapped.trust)}
                onClick={() =>
                  handleBlockClick(
                    mapped.trust.section.type,
                    mapped.trust.index
                  )
                }
              >
                <AiTrustSignalsRow
                  section={mapped.trust.section}
                  text={mapped.trust.section?.text}
                  isMobile={isMobile}
                />
              </PreviewSlot>
            ) : null}

            {isBrandVisible ? (
              <PreviewSlot
                className={styles.footerRow}
                isSelected={isSelected(mapped.brandAttribution)}
                onClick={() =>
                  handleBlockClick(
                    mapped.brandAttribution.section.type,
                    mapped.brandAttribution.index
                  )
                }
              >
                <AiBrandAttribution
                  section={mapped.brandAttribution.section}
                  forceShowAttribution={appState?.is_preview_mode}
                  forceShowBadge={appState?.is_preview_mode}
                />
              </PreviewSlot>
            ) : null}
          </>
        )}

        {showFloatingWatermark ? (
          <PopupWatermark
            popupData={popupData}
            activeStep={activeStep}
            visible={showFloatingWatermark}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ProductRecommendationPopup;
